import React from 'react';
import { WEARABLE_PROVIDERS } from '@/config/wearableProviders';

interface DataSourceFilterProps {
    selectedSources: string[];
    onSourceChange: (sources: string[]) => void;
    connectedSources?: string[];
}

const DataSourceFilter: React.FC<DataSourceFilterProps> = ({
    selectedSources,
    onSourceChange,
    connectedSources,
}) => {
    const providers = WEARABLE_PROVIDERS.filter(p => p.enabled);

    const toggleSource = (sourceId: string) => {
        // Only allow toggling connected sources
        if (connectedSources && !connectedSources.includes(sourceId)) return;

        if (selectedSources.includes(sourceId)) {
            // Don't allow deselecting if it's the only one selected
            if (selectedSources.length > 1) {
                onSourceChange(selectedSources.filter((s) => s !== sourceId));
            }
        } else {
            onSourceChange([...selectedSources, sourceId]);
        }
    };

    return (
        <div className="mb-4 transition-all duration-300">
            <div className="cgm-card rounded-xl p-1.5 px-3">
                <div className="flex items-center w-full gap-2">
                    <span className="text-xs font-medium cgm-text-secondary flex-shrink-0">Source</span>
                    <div className="flex space-x-2 items-center">
                        {providers.map((provider) => {
                            const isConnected = !connectedSources || connectedSources.includes(provider.id);
                            const isSelected = isConnected && selectedSources.includes(provider.id);
                            // Use alt logo (white variant) when provider has a dark background
                            const logoSrc = provider.logoStyle?.background && provider.logoAlt
                                ? provider.logoAlt
                                : provider.logo;

                            return (
                                <button
                                    key={provider.id}
                                    onClick={() => toggleSource(provider.id)}
                                    className={`relative rounded-xl transition-all duration-200 ${isConnected ? 'hover:scale-110 cursor-pointer' : 'cursor-default'}`}
                                    title={isConnected ? `Toggle ${provider.label} data` : `${provider.label} not connected`}
                                    style={{
                                        opacity: isConnected ? (isSelected ? 1 : 0.4) : 0.15,
                                        filter: isConnected ? 'none' : 'grayscale(100%)',
                                    }}
                                >
                                    <div
                                        style={{
                                            width: 30,
                                            height: 30,
                                            borderRadius: provider.logoStyle?.borderRadius || 4,
                                            background: provider.logoStyle?.background || 'transparent',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <img
                                            src={logoSrc}
                                            alt={`${provider.label} logo`}
                                            style={{
                                                width: provider.logoStyle?.background ? 20 : 30,
                                                height: provider.logoStyle?.background ? 20 : 30,
                                                objectFit: 'contain',
                                            }}
                                        />
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DataSourceFilter;
